import { frameTemplateFor, liveTemplateFor, subscribeFrames } from '../../../../lib/timeLayers/frameStore'
import { opacityFor, subscribeOpacity } from '../../../../lib/timeLayers/opacityStore'

/**
 * 2D overlay for a time-animated weather layer (the layer has a `timeStep`, or a pmtiles
 * URL with a `default` frame segment).
 *
 * A custom MapType rather than an ImageMapType: an ImageMapType can only change frames by
 * being swapped out of overlayMapTypes, which blanks every tile until the new frame
 * arrives. Here each tile is a div that keeps showing its current image while the next
 * frame's image loads underneath, so play and scrubbing never flash.
 *
 * The frame comes from the frame store (the time bar is its only writer); with no entry the
 * layer draws its live `default` tiles, exactly as multiTileLayersXYZ would.
 *
 * Like multiTileLayersXYZ this factory does not touch overlayMapTypes: drawOverlays is the
 * sole writer. drawOverlays builds a fresh overlay on every render and discards all but
 * the one it places, so construction has no side effects; tiles register themselves when
 * Google asks the placed overlay for them.
 */

const TILE = 256
const tilesByLayer = new Map()   // layerId -> Set of tile records

function tileSet(layerId) {
  let set = tilesByLayer.get(layerId)
  if (!set) {
    set = new Set()
    tilesByLayer.set(layerId, set)
  }
  return set
}

function liveTiles(layerId) {
  const set = tilesByLayer.get(layerId)
  if (!set) return []
  const out = []
  set.forEach(rec => {
    if (rec.div.isConnected) out.push(rec)
    else if (rec.seen) set.delete(rec)   // released without releaseTile
  })
  return out
}

/** Concrete URL of one tile from a {z}/{x}/{y} template; y goes out as TMS. */
export function tileUrlFrom(template, coord, zoom) {
  const n = 1 << zoom
  if (coord.y < 0 || coord.y >= n) return null
  const x = ((coord.x % n) + n) % n
  return template
    .replace('{z}', zoom)
    .replace('{x}', x)
    .replace('{y}', n - 1 - coord.y)
}

function showFrame(rec) {
  const template = frameTemplateFor(rec.layerId) || liveTemplateFor(rec.layerId, rec.liveTemplate)
  const url = template ? tileUrlFrom(template, rec.coord, rec.zoom) : null
  if (url === rec.url) return
  rec.url = url
  if (rec.pending) {
    rec.pending.onload = rec.pending.onerror = null
    rec.pending.remove()
    rec.pending = null
  }
  if (!url) {
    if (rec.current) rec.current.remove()
    rec.current = null
    return
  }
  const img = rec.div.ownerDocument.createElement('img')
  img.alt = ''
  img.draggable = false
  img.style.cssText = `position:absolute;left:0;top:0;width:${TILE}px;height:${TILE}px;visibility:hidden;`
  img.onload = () => {
    if (rec.pending !== img) return
    rec.pending = null
    if (rec.current) rec.current.remove()
    img.style.visibility = 'visible'
    rec.current = img
  }
  img.onerror = () => {
    // A frame with no tile here (outside coverage, or an unlisted frame) draws nothing.
    if (rec.pending !== img) return
    rec.pending = null
    img.remove()
    if (rec.current) rec.current.remove()
    rec.current = null
  }
  rec.pending = img
  rec.div.appendChild(img)
  img.src = url
}

subscribeFrames(changed => {
  changed.forEach(layerId => liveTiles(layerId).forEach(showFrame))
})

// The time bar's opacity slider: restyle the layer's tiles in place.
subscribeOpacity((layerId, opacity) => {
  liveTiles(layerId).forEach(rec => { rec.div.style.opacity = opacity })
})

// An MVCObject like Google's own map types, because the layer code stamps every overlay with
// set('overlayIndex') when it adds map labels; a plain object threw there and blanked the map.
export function timeLayerOverlay(mapObj, { liveTemplate, maxZoom }) {
  return Object.assign(new google.maps.MVCObject(), {
    tileSize: new google.maps.Size(TILE, TILE),
    minZoom: 3,
    maxZoom,
    name: mapObj.name,
    alt: {
      id: mapObj?.id,
      order: mapObj?.order,
      drawOrder: mapObj?.drawOrder,
      timeLayer: true
    },
    getTile(coord, zoom, doc) {
      const div = doc.createElement('div')
      div.style.cssText = `position:relative;width:${TILE}px;height:${TILE}px;overflow:hidden;opacity:${opacityFor(mapObj.id)};`
      const rec = { layerId: mapObj.id, liveTemplate, coord: { x: coord.x, y: coord.y }, zoom, div, url: undefined, current: null, pending: null, seen: false }
      div.__timeTile = rec
      tileSet(mapObj.id).add(rec)
      showFrame(rec)
      // Google attaches the div after getTile returns; only prune it once it has been seen.
      setTimeout(() => { rec.seen = true }, 0)
      return div
    },
    releaseTile(div) {
      const rec = div && div.__timeTile
      if (!rec) return
      if (rec.pending) rec.pending.onload = rec.pending.onerror = null
      tilesByLayer.get(rec.layerId)?.delete(rec)
    }
  })
}

/**
 * Warms the browser cache with `frames` ([{layerId, template}]) for every tile of those
 * layers on screen. Calls `onProgress(done / total)` and resolves {loaded, failed}.
 * `cancel()` on the returned handle stops counting (requests already sent still finish).
 */
export function prefetchFrames(frames, onProgress) {
  let cancelled = false
  const jobs = []
  frames.forEach(({ layerId, template }) => {
    liveTiles(layerId).forEach(rec => {
      const url = tileUrlFrom(template, rec.coord, rec.zoom)
      if (url) jobs.push(url)
    })
  })
  const urls = [...new Set(jobs)]
  let loaded = 0, failed = 0
  const promise = new Promise(resolve => {
    if (urls.length === 0) return resolve({ loaded, failed })
    const done = ok => {
      if (cancelled) return
      if (ok) loaded++; else failed++
      onProgress && onProgress((loaded + failed) / urls.length)
      if (loaded + failed === urls.length) resolve({ loaded, failed })
    }
    urls.forEach(url => {
      const img = new Image()
      img.onload = () => done(true)
      img.onerror = () => done(false)
      img.src = url
    })
  })
  return { promise, cancel: () => { cancelled = true } }
}
